import { LocalDate, LocalDateTime } from "@js-joda/core";
import React from "react";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import { EntityProfileEmptyState } from "../../../../entityProfileModal/components/EntityProfileFeedback";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import type { PatientId } from "../../../../../shared/schema/schema";
import { formatPatientProfileValue } from "../../../patientProfileModal.utils";
import { usePatientAssociatedCaregivers } from "./PatientProfileCalendarCaregiversSection.hooks";
import type { PatientAssociatedCaregiver } from "./patientCalendarCaregivers.types";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";

type Props = {
  actions: PatientProfileBridgeActions;
  patientId: PatientId;
};

type SortKey = "caregiver" | "from" | "to" | "visitsCount";
const caregiverPageSizeOptions = [15, 25, 50, 100];

function getCaregiverDisplayName(caregiver: PatientAssociatedCaregiver) {
  return caregiver.displayName ?? `Caregiver #${caregiver.caregiverId}`;
}

function getCaregiverDisplayId(caregiver: PatientAssociatedCaregiver) {
  return caregiver.displayId ?? caregiver.caregiverId;
}

function getCaregiverLabel(caregiver: PatientAssociatedCaregiver) {
  const displayName = getCaregiverDisplayName(caregiver);
  const displayId = getCaregiverDisplayId(caregiver);

  return displayName.includes(`(${displayId})`) ? displayName : `${displayName} (${displayId})`;
}

function formatDate(value: unknown) {
  if (formatPatientProfileValue(value) === "") {
    return "";
  }

  if (value instanceof LocalDate || value instanceof LocalDateTime) {
    return dateFormatter.toDate(value);
  }

  if (typeof value === "string") {
    try {
      return dateFormatter.toDate(LocalDate.parse(value.slice(0, 10)));
    } catch {
      return value;
    }
  }

  return String(value);
}

function getDateSortValue(value: unknown) {
  if (value instanceof LocalDate) {
    return value.toString();
  }

  if (value instanceof LocalDateTime) {
    return value.toLocalDate().toString();
  }

  if (typeof value === "string") {
    return value.slice(0, 10);
  }

  return "";
}

export default function PatientProfileCalendarCaregiversSection(props: Props) {
  const caregiversQuery = usePatientAssociatedCaregivers(props.patientId);
  const caregivers = caregiversQuery.data?.caregivers ?? [];
  const columns = React.useMemo<
    Array<EntityProfileDataTableColumn<PatientAssociatedCaregiver, SortKey>>
  >(
    () => [
      {
        key: "caregiver",
        header: "Caregiver",
        render: (caregiver) => (
          <EntityModalButton
            kind="blue"
            style={{ color: "#0f172a", fontSize: "14px" }}
            variant="link"
            onClick={() => props.actions.navigateToCaregiver(caregiver.caregiverId)}
          >
            {getCaregiverLabel(caregiver)}
          </EntityModalButton>
        ),
        sortKey: "caregiver",
        sortValue: getCaregiverLabel,
        cellProps: { borderColor: "#eee", fontSize: "14px", py: "9px" },
      },
      {
        key: "from",
        header: "Start date",
        render: (caregiver) => formatDate(caregiver.from),
        sortKey: "from",
        sortValue: (caregiver) => getDateSortValue(caregiver.from),
        cellProps: { borderColor: "#eee", fontSize: "14px", py: "9px" },
      },
      {
        key: "to",
        header: "End date",
        render: (caregiver) => formatDate(caregiver.to),
        sortKey: "to",
        sortValue: (caregiver) => getDateSortValue(caregiver.to),
        cellProps: { borderColor: "#eee", fontSize: "14px", py: "9px" },
      },
      {
        key: "visitsCount",
        header: "Visits Count",
        render: (caregiver) => caregiver.visitsCount,
        sortKey: "visitsCount",
        sortValue: (caregiver) => caregiver.visitsCount ?? 0,
        cellProps: { borderColor: "#eee", fontSize: "14px", py: "9px" },
      },
    ],
    [props.actions]
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load associated caregivers."
      isError={caregiversQuery.isError}
      isLoading={caregiversQuery.isLoading}
      loadingLabel="Loading associated caregivers"
      onRetry={() => void caregiversQuery.refetch()}
    >
      {caregivers.length === 0 ? (
        <EntityProfileEmptyState>No associated caregivers.</EntityProfileEmptyState>
      ) : (
        <EntityProfileDataTable
          columns={columns}
          defaultPageSize={15}
          defaultSort={{ key: "caregiver", direction: "asc" }}
          getRowKey={(caregiver) => caregiver.caregiverId}
          pageSizeOptions={caregiverPageSizeOptions}
          rows={caregivers}
        />
      )}
    </EntityProfileQuerySection>
  );
}
